"use client";

/* eslint-disable react/prop-types */

import BFLLink from "@repo/shared-ui/BFLLink";
import { addStash } from "@repo/shared-utils/common-utils";
import { FC, memo, useMemo } from "react";
import { IStockListingRow } from "../../interfaces/StockListingTable.interface";
import type { StockListingUILabels } from "../../interfaces/StockListingTableAEM.interface";
import { formatPriceChange } from "../../utils/stockPriceUtils";

export interface StockTableRowProps {
  /** Stock item data */
  item: IStockListingRow;
  /** Unique key for the row */
  rowKey: string;
  /** Whether this is a skeleton/loading state */
  isSkeleton: boolean;
  /** Variant-specific CSS classes */
  classes: {
    tdPaddingClass: string;
    tdRightClass: string;
    symbolTextClass: string;
    nameTextClass: string;
    ltpTextClass: string;
    changeTextClass: string;
  };
  /** AEM UI labels */
  uiLabels?: StockListingUILabels;
  /** Called with the stock symbol when a row is clicked (used for CT tracking) */
  onStockClick?: (stockName: string) => void;
  /** Row click handler */
  onRowClick?: (e: React.MouseEvent) => void;
  /** Link click handler */
  onLinkClick?: (e: React.MouseEvent) => void;
  /** Bookmark click handler */
  onBookmarkClick?: (e: React.MouseEvent, item: IStockListingRow) => void;
  /** Bookmark mouse down handler */
  onBookmarkMouseDown?: (e: React.MouseEvent) => void;
  /** Bookmark mouse up handler */
  onBookmarkMouseUp?: (e: React.MouseEvent) => void;
}

/**
 * Renders a single stock table row with company name and price cells.
 * Handles both skeleton loading state and actual data display.
 */
  const StockTableRow: FC<StockTableRowProps> = memo(
  ({
    item,
    rowKey,
    isSkeleton,
    classes,
    uiLabels = {},
    onBookmarkClick,
    onBookmarkMouseDown,
    onBookmarkMouseUp,
  }) => {

    const bookmarkAriaLabel = useMemo(
      () =>
        (uiLabels.addToWatchlistAriaLabel ?? "Add {symbol} to watchlist").replace(
          "{symbol}",
          item.symbol ?? "",
        ),
      [uiLabels.addToWatchlistAriaLabel, item.symbol],
    );

    const {
      tdPaddingClass,
      tdRightClass,
      symbolTextClass,
      nameTextClass,
      ltpTextClass,
      changeTextClass,
    } = classes;

    const changeText = useMemo(() => formatPriceChange(item, isSkeleton), [item, isSkeleton]);
    const isNegative = useMemo(() => {
      const perChangeNumber = Number(String(item.perChange ?? "").replace("%", "").replaceAll(",", "").trim());
      if (Number.isFinite(perChangeNumber)) {
        return perChangeNumber < 0;
      }

      const changePriceNumber = Number(String(item.changePrice ?? "").replace("%", "").replaceAll(",", "").trim());
      if (Number.isFinite(changePriceNumber)) {
        return changePriceNumber < 0;
      }

      return false;
    }, [item.perChange, item.changePrice]);
    const trendColorClass = isNegative ? "text-[#b40000]" : "text-[rgb(6,113,97)]";
    const trendArrowClass = isNegative
      ? String.raw`before:content-['\e981']`
      : String.raw`before:content-['\e980']`;

    const renderCompanyNameCell = () => (
      <div className="min-w-0 flex flex-col items-start">
        <p
          className={`box-border block h-[19px] m-0 cursor-pointer mb-1 truncate ${symbolTextClass} font-[Rubik] font-medium leading-normal text-[rgb(26,26,26)] ${
            isSkeleton ? "bg-gray-200 text-transparent" : ""
          }`}
        >
          {isSkeleton ? "Loading symbol" : item.symbol}
        </p>
        <p
          className={`box-border block h-[16.5px] m-0 cursor-pointer truncate ${nameTextClass} font-normal leading-normal text-[rgb(102,102,102)] ${
            isSkeleton ? "bg-gray-100 text-transparent" : ""
          }`}
        >
          {isSkeleton ? "Loading name" : item.fullName}
        </p>
      </div>
    );

    const renderPriceCell = () => (
      <div className="flex flex-col items-end">
        <div className="flex items-center gap-[5px] mb-1">
          <p
            className={`box-border block h-[19px] m-0 cursor-pointer ${ltpTextClass} font-normal leading-normal ${trendColorClass} text-end ${
              isSkeleton ? "bg-gray-200 text-transparent" : ""
            }`}
          >
            {isSkeleton ? "0.00" : item.ltp}
          </p>
          {!isSkeleton && (
            <i className={`icomoon text-[17px] md:text-[20px] ${trendColorClass} ${trendArrowClass}`} />
          )}
        </div>
        <p
          className={`box-border block h-[16.5px] m-0 cursor-pointer ${changeTextClass} font-normal leading-normal text-[rgb(102,102,102)] ${
            isSkeleton ? "bg-gray-200 text-transparent" : ""
          }`}
        >
          {changeText}
        </p>
      </div>
    );
    
    const rowHref = useMemo(() => {
      if (!item.redirectURL) return undefined;
      return addStash(item.redirectURL);
    }, [item.redirectURL]);

    const rowContent = (
      <tr
        key={rowKey}
        data-stock-row="true"
        className={`box-border h-[72.5px] cursor-pointer border-b border-b-[rgb(229,229,229)] leading-[19.2px] ${
          isSkeleton ? "animate-pulse" : ""
        }`}
        title={isSkeleton ? "" : item.fullName ?? ""}
      >
        <td className={`${tdPaddingClass} ${tdRightClass}`} colSpan={2}>
          <section className="flex items-center justify-between w-full gap-3">
            <BFLLink
              className="block w-full min-w-0"
              href={rowHref}
            >
              <section className="flex items-center justify-between w-full gap-3">
                {renderCompanyNameCell()}
                {renderPriceCell()}
              </section>
            </BFLLink>
            <button
              type="button"
              className="ml-2 shrink-0 rounded flex h-[20px] w-[20px] cursor-pointer items-center justify-center transition-opacity hover:opacity-70"
              aria-label={bookmarkAriaLabel}
              data-bookmark-container="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmarkClick?.(e, item);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmarkMouseDown?.(e);
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmarkMouseUp?.(e);
              }}
              onTouchStart={(e) => {
                // Do not call preventDefault so the subsequent click event is still fired
                e.stopPropagation();
                onBookmarkMouseDown?.(e as unknown as React.MouseEvent);
              }}
              onTouchEnd={(e) => {
                // Do not call preventDefault so the subsequent click event is still fired
                e.stopPropagation();
                onBookmarkMouseUp?.(e as unknown as React.MouseEvent);
              }}
            >
              <i className="icomoon text-[16px] text-black before:content-['\e95C']" />
            </button>
          </section>
        </td>
      </tr>
    );
    
    return rowContent;
  },
);

StockTableRow.displayName = "StockTableRow";

export default StockTableRow;
